import { cache, use } from 'react';
import { GlobalVar, TaskVar } from '@frameio/async-hooks/task-var';
import { type Future, future, present } from '@frameio/basics/async/future';
import { notFound } from './not-found';
import { type IntlConfig, type IntlShape } from './types';

export { createIntl } from './create';
export type { IntlConfig, IntlShape };

const INTL = new TaskVar<IntlShape>();

const INTL_CACHE = cache(() => new GlobalVar<IntlShape>());

/**
 * Returns the best available Intl context.
 *
 * `intl()` looks for contexts in the following places:
 *   1. First it calls `ltl()`
 *     a. `getIntl()` returns the active Intl context from `withIntl`
 *     b. failing that, the result of `use(IntlContext)`
 *
 *   2. If `getIntl()` returns undefined, `intl()` returns the result of `notFound()`:
 *     a. this will return the last fallback registered with `provideFallback`
 *     b. if no fallbacks are registered, it will return the default fallback, which
 *        is a basic `en` context.
 *
 * On the server, fallbacks are task-local. On the client, they're global.
 *
 * @returns
 */
export default function intl(): IntlShape {
  const provider = getIntl() ?? notFound();
  return provider;
}

/**
 * Returns the current Intl context from either React or `withIntl`, or undefined.
 *
 * @returns the current active Intl context, or undefined.
 */
export function getIntl(): IntlShape | undefined {
  for (const finder of PATH) {
    const result = finder.data?.();
    if (result) {
      return result;
    }
  }
  return undefined;
}

type IntlFinder = () => IntlShape | undefined;

export const PATH: Future<IntlFinder>[] = [
  present(tryFromTaskVar),
  present(tryFromReactServerCache),
  future(
    import('react-intl')
      .then(({ IntlContext }) => tryFromReactClientContext(IntlContext))
      .catch(() => {
        return () => undefined;
      })
  ),
];

export const READY = future(Promise.all(PATH));

export function ready() {
  return READY;
}

function tryFromTaskVar() {
  return INTL.getStore();
}

function tryFromReactServerCache() {
  return INTL_CACHE().getStore();
}

function tryFromReactClientContext(context: any): () => IntlShape | undefined {
  return function tryFromTheReactClientContext() {
    const { error } = console;
    try {
      // suppress react's hook invariant error -- we know we're being bad
      // eslint-disable-next-line @typescript-eslint/no-empty-function, no-console
      console.error = () => {
        return undefined;
      };
      return use(context);
    } catch (e: any) {
      return undefined;
    } finally {
      // eslint-disable-next-line no-console
      console.error = error;
    }
  };
}

export function withIntl<R, TArgs extends any[]>(
  store: IntlShape | undefined,
  fn: (...args: TArgs) => R,
  ...args: TArgs
): R {
  if (!store) {
    return fn(...args);
  }
  return INTL_CACHE().run(store, () => INTL.run(store, fn, ...args));
}

export function enterWithIntl(store: IntlShape): void {
  INTL_CACHE().enterGlobal(store);
  if (INTL.isAsync) {
    INTL.enterWith(store);
  }
}

export const BOUND_INTL = Symbol('bound intl context');
